import { Feature } from "common/types";
import { Optional } from "common/utils/types";
import { US_STATES } from "common/constants/address";

import { ControlledSubstancesSummary } from "client/carts/types";
import { useFeatures } from "client/clinics/hooks";
import TextButton from "client/common/components/buttons/TextButton";

import { useControlledSubstanceGuidanceDrawer } from "../stackables";
import { getControlledProductLinesText } from "../utils";

type ControlledSubstanceStateNoticeProps = {
  summary: Optional<ControlledSubstancesSummary>;
  productLineNames: string[];
};

/**
 * Names the state a prescription's controlled-substance rules are judged against, so the
 * client can tell that changing where the order goes can change what is required of it.
 *
 * Renders nothing off the destination basis: `basis_state` is then the client's own
 * primary address, which no delivery address can change, so the sentence would promise a
 * variability that does not exist.
 */
const ControlledSubstanceStateNotice = ({
  summary,
  productLineNames,
}: ControlledSubstanceStateNoticeProps) => {
  const isDestinationBasisEnabled = useFeatures()[Feature.ControlledSubstanceShippingAddressBasis];
  const { open: openControlledSubstanceGuidanceDrawer } = useControlledSubstanceGuidanceDrawer();

  const stateName = summary?.basis_state ? US_STATES[summary.basis_state] : undefined;
  if (
    !isDestinationBasisEnabled ||
    !summary?.is_controlled ||
    !stateName ||
    !productLineNames.length
  ) {
    return null;
  }

  return (
    <>
      {getControlledProductLinesText(productLineNames)} in {stateName}. Requirements can be
      different depending on where your order is delivered.{"\u00a0"}
      <TextButton hasUnderline={true} icon="right" onClick={openControlledSubstanceGuidanceDrawer}>
        Learn more
      </TextButton>
    </>
  );
};

export default ControlledSubstanceStateNotice;
